import { useMemo } from 'react';
import dayjs from 'dayjs';
import { Alert, Button, Card, Checkbox, Col, Collapse, Empty, List, Row, Segmented, Space, Spin, Statistic, Table, Tag, Typography } from 'antd';
import { CalendarOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { Column } from '@ant-design/plots';
import type { CalendarEvent, LeadTodo, LeadTodoPriority, PipelineHistoryResponse } from './types';
import type { LeadTier } from './constants';
import {
	formatAmount,
	formatDateTimeFull,
	formatDateTimeShort,
	statusDefinitions,
	statusLabelMap,
	statusChartColors,
	statusTagColors,
	todoPriorityColors,
	todoPriorityLabels
} from './constants';

const { Text } = Typography;

const PIPELINE_WINDOW_OPTIONS = [
	{ label: '7 dage', value: 7 },
	{ label: '14 dage', value: 14 },
	{ label: '30 dage', value: 30 }
];

const IGNORED_PIPELINE_STATUSES = new Set(['REFERRED']);

type DashboardRecentLead = {
	id: string;
	company_name?: string | null;
	contact_name?: string | null;
	status?: string | null;
	updated_at?: string | null;
	last_policy_activity_at?: string | null;
	created_at?: string | null;
	cvrnummer?: string | null;
	created_by_name?: string | null;
	updated_by_name?: string | null;
};

type DashboardPropertyPortfolio = {
	leadId: string;
	companyName: string | null;
	cvrnummer: string;
	status: string | null;
	buildingCount: number;
	totalBuildingArea: number;
	totalResidentialArea: number;
	totalCommercialArea: number;
};

type DashboardPropertyAlert = {
	id: string;
	leadId: string | null;
	companyName: string | null;
	cvrnummer: string;
	severity: 'HIGH' | 'CRITICAL';
	title: string;
	description: string | null;
	sourceRegisters: string[];
	detectedAt: string;
};

type DashboardPageProps = {
	earned: number;
	activeLeadCount: number;
	tierCounts: Record<LeadTier, number>;
	recentLeads: DashboardRecentLead[];
	recentLeadsLoading: boolean;
	propertyPortfolios: DashboardPropertyPortfolio[];
	propertyPortfoliosLoading: boolean;
	propertyPortfoliosError: string | null;
	propertyAlerts: DashboardPropertyAlert[];
	propertyAlertsLoading: boolean;
	includeHiddenRecentLeads: boolean;
	upcomingMeetings: CalendarEvent[];
	upcomingMeetingsLoading: boolean;
	meetingWindowDays: number;
	upcomingTodos: LeadTodo[];
	upcomingTodosLoading: boolean;
	todoWindowDays: number;
	canManageTodos: boolean;
	showPipelineHistory: boolean;
	pipelineHistory: PipelineHistoryResponse | null;
	pipelineHistoryLoading: boolean;
	pipelineHistoryError: string | null;
	pipelineHistoryWindow: number;
	onPipelineWindowChange: (days: number) => void;
	onIncludeHiddenRecentLeadsChange: (checked: boolean) => void;
	onOpenLead: (leadId: string) => void;
	onOpenCompany: (cvrnummer: string) => void;
	onOpenLeadTier: (tier: LeadTier) => void;
	onGoToCalendar: () => void;
	onGoToTodos: () => void;
};

export default function DashboardPage({
	earned,
	activeLeadCount,
	tierCounts,
	recentLeads,
	recentLeadsLoading,
	propertyPortfolios,
	propertyPortfoliosLoading,
	propertyPortfoliosError,
	propertyAlerts,
	propertyAlertsLoading,
	includeHiddenRecentLeads,
	upcomingMeetings,
	upcomingMeetingsLoading,
	meetingWindowDays,
	upcomingTodos,
	upcomingTodosLoading,
	todoWindowDays,
	canManageTodos,
	showPipelineHistory,
	pipelineHistory,
	pipelineHistoryLoading,
	pipelineHistoryError,
	pipelineHistoryWindow,
	onPipelineWindowChange,
	onIncludeHiddenRecentLeadsChange,
	onOpenLead,
	onOpenCompany,
	onOpenLeadTier,
	onGoToCalendar,
	onGoToTodos
}: DashboardPageProps) {
	const pipelinePoints = pipelineHistory?.points;
	const formatArea = (value: number) => `${new Intl.NumberFormat('da-DK', { maximumFractionDigits: 0 }).format(value)} m2`;
	const pipelineSeries = useMemo(() => {
		if (!pipelinePoints || pipelinePoints.length === 0) {
			return [] as Array<{
				status: string;
				label: string;
				color: string;
				points: { day: string; count: number }[];
				latestCount: number;
				dailyPoints: { day: string; value: number }[];
				latestDailyCount: number;
				totalCount: number;
			}>;
		}
		const fallbackStatuses = statusDefinitions.map((item) => item.value);
		const statuses =
			Array.isArray(pipelineHistory?.statuses) && pipelineHistory.statuses.length > 0
				? pipelineHistory.statuses
				: fallbackStatuses;
		return statuses
			.filter((status) => !IGNORED_PIPELINE_STATUSES.has(status))
			.map((status) => {
			const seriesPoints = pipelinePoints
				.filter((point) => point.status === status)
				.map((point) => ({ day: point.day, count: point.count }))
				.sort((a, b) => a.day.localeCompare(b.day));
			const latestCount = seriesPoints.length > 0 ? seriesPoints[seriesPoints.length - 1].count : 0;
			let previousCount = 0;
			let latestDailyCount = 0;
			let totalCount = 0;
			const dailyPoints = seriesPoints.map((point) => {
				const delta = Math.max(point.count - previousCount, 0);
				previousCount = point.count;
				latestDailyCount = delta;
				totalCount += delta;
				return { day: point.day, value: delta };
			});
			return {
				status,
				label: statusLabelMap.get(status) || status,
				color: statusChartColors[status] || '#5B8FF9',
				points: seriesPoints,
				dailyPoints,
				latestCount,
				latestDailyCount,
				totalCount
			};
			});
	}, [pipelinePoints, pipelineHistory?.statuses]);

	const pipelineColorMap = useMemo(() => {
		return pipelineSeries.reduce((acc, series) => {
			acc[series.status] = series.color;
			return acc;
		}, {} as Record<string, string>);
	}, [pipelineSeries]);

	const pipelineChartData = useMemo(() => {
		return pipelineSeries.flatMap((series) =>
			series.dailyPoints.map((point) => ({
				day: point.day,
				value: point.value,
				status: series.status,
				label: series.label
			}))
		);
	}, [pipelineSeries]);

	const hasPipelineCounts = pipelineChartData.some((point) => point.value > 0);
	const overdueTodos = useMemo(
		() =>
			upcomingTodos.filter((todo) => {
				const now = dayjs();
				if (!todo.due_at) {
					return false;
				}
				const due = dayjs(todo.due_at);
				return due.isValid() && due.isBefore(now);
			}),
		[upcomingTodos]
	);
	const nonOverdueTodos = useMemo(
		() =>
			upcomingTodos.filter((todo) => {
				const now = dayjs();
				if (!todo.due_at) {
					return true;
				}
				const due = dayjs(todo.due_at);
				return !due.isValid() || !due.isBefore(now);
			}),
		[upcomingTodos]
	);
	const attentionItems = useMemo(() => {
		const items: Array<{
			key: string;
			icon: React.ReactNode;
			title: string;
			description: string;
			actionLabel?: string;
			onClick?: () => void;
			danger?: boolean;
		}> = [];
		if (overdueTodos.length > 0) {
			items.push({
				key: 'overdue-todos',
				icon: <ExclamationCircleOutlined />,
				title: `${overdueTodos.length} todo${overdueTodos.length === 1 ? '' : 's'} har overskredet deadline`,
				description: overdueTodos[0]?.title || 'Se overskredne todos',
				actionLabel: 'Se todos',
				onClick: onGoToTodos,
				danger: true
			});
		}
		for (const alert of propertyAlerts) {
			items.push({
				key: `property-alert-${alert.id}`,
				icon: <ExclamationCircleOutlined />,
				title: `${alert.severity === 'CRITICAL' ? 'Kritisk' : 'Væsentlig'} ejendomsændring: ${alert.title}`,
				description: `${alert.companyName || 'Ukendt virksomhed'} · CVR ${alert.cvrnummer} · ${formatDateTimeShort(alert.detectedAt)}`,
				actionLabel: alert.leadId ? 'Åbn lead' : 'Slå virksomhed op',
				onClick: alert.leadId ? () => onOpenLead(alert.leadId as string) : () => onOpenCompany(alert.cvrnummer),
				danger: true
			});
		}
		if (nonOverdueTodos.length > 0) {
			const nextTodo = nonOverdueTodos[0];
			items.push({
				key: 'next-todo',
				icon: <CheckCircleOutlined />,
				title: 'Næste todo',
				description: nextTodo.title,
				actionLabel: 'Se todos',
				onClick: onGoToTodos
			});
		}
		if (upcomingMeetings.length > 0) {
			const nextMeeting = upcomingMeetings[0];
			items.push({
				key: 'next-meeting',
				icon: <CalendarOutlined />,
				title: 'Næste møde',
				description: `${nextMeeting.title || nextMeeting.leadCompanyName || 'Møde'} · ${formatDateTimeShort(nextMeeting.startAt)}`,
				actionLabel: 'Åbn kalender',
				onClick: onGoToCalendar
			});
		}
		return items;
	}, [nonOverdueTodos, onGoToCalendar, onGoToTodos, onOpenCompany, onOpenLead, overdueTodos, propertyAlerts, upcomingMeetings]);

	const renderTodoListItem = (todo: LeadTodo) => {
		const priority = (todo.priority || 'MEDIUM') as LeadTodoPriority;
		const leadId = todo.lead_id;
		return (
			<List.Item
				actions={
					leadId
						? [
								<Button key="todo-open-lead" size="small" onClick={() => onOpenLead(leadId)}>
									Åbn lead
								</Button>
							]
						: undefined
				}
			>
				<List.Item.Meta
					title={
						<Space size={8} align="center">
							<Tag color={todoPriorityColors[priority]}>{todoPriorityLabels[priority]}</Tag>
							<span>{todo.title}</span>
						</Space>
					}
					description={
						<Space direction="vertical" size={0}>
							<Text strong>{todo.due_at ? formatDateTimeShort(todo.due_at) : 'Ingen deadline'}</Text>
							{todo.lead_company_name && <Text type="secondary">{todo.lead_company_name}</Text>}
							{todo.assigned_to_name && <Text type="secondary">{todo.assigned_to_name}</Text>}
						</Space>
					}
				/>
			</List.Item>
		);
	};

	const renderTierStatistic = (title: string, value: number, tier: LeadTier) => (
		<Button
			type="text"
			onClick={() => onOpenLeadTier(tier)}
			style={{ height: 'auto', padding: 0, textAlign: 'left' }}
		>
			<Statistic title={title} value={value} />
		</Button>
	);

	return (
		<Space direction="vertical" size={24} className="dashboard-layout" style={{ width: '100%' }}>
			<div className="dashboard-page-header">
				<div>
					<Typography.Title level={2}>Dashboard</Typography.Title>
					<Text type="secondary">Dit overblik over leads, opgaver og kommende aftaler.</Text>
				</div>
				<Button type="primary" onClick={onGoToCalendar}>
					Åbn kalender
				</Button>
			</div>
			<Card
				className="dashboard-attention-card"
				title="Kræver din opmærksomhed"
				extra={attentionItems.length > 0 ? <Text type="secondary">{attentionItems.length} punkter</Text> : null}
			>
				{propertyAlertsLoading ? (
					<div style={{ padding: 24, textAlign: 'center' }}><Spin /></div>
				) : attentionItems.length === 0 ? (
					<Empty description="Du er ajour" image={Empty.PRESENTED_IMAGE_SIMPLE} />
				) : (
					<List
						dataSource={attentionItems}
						rowKey={(item) => item.key}
						renderItem={(item) => (
							<List.Item
								className={item.danger ? 'dashboard-attention-item is-danger' : 'dashboard-attention-item'}
								actions={item.onClick && item.actionLabel ? [<Button key={`${item.key}-action`} type="link" onClick={item.onClick}>{item.actionLabel}</Button>] : undefined}
							>
								<List.Item.Meta
									avatar={<span className="dashboard-attention-icon">{item.icon}</span>}
									title={item.title}
									description={item.description}
								/>
							</List.Item>
						)}
					/>
				)}
			</Card>
			<Row gutter={16}>
				<Col>
					<Statistic
						title="Samlet provision"
						value={earned}
						formatter={(value) => formatAmount(value as number, 'DKK')}
					/>
				</Col>
				<Col>
					<Statistic title="Pipeline" value={activeLeadCount} />
				</Col>
				<Col>
					{renderTierStatistic('Leads', tierCounts.LEAD, 'LEAD')}
				</Col>
				<Col>
					{renderTierStatistic('Sager', tierCounts.IN_PROGRESS, 'IN_PROGRESS')}
				</Col>
				<Col>
					{renderTierStatistic('Kunder', tierCounts.CUSTOMER, 'CUSTOMER')}
				</Col>
			</Row>

			<Row gutter={[16, 16]}>
				<Col xs={24} lg={12}>
					<Card
						title={`Kommende møder · næste ${meetingWindowDays} dage`}
						extra={
							<Button type="link" size="small" onClick={onGoToCalendar}>
								Gå til kalender
							</Button>
						}
					>
						{upcomingMeetingsLoading ? (
							<div style={{ padding: 24, textAlign: 'center' }}>
								<Spin />
							</div>
						) : upcomingMeetings.length === 0 ? (
							<Empty
								description="Ingen planlagte møder i intervallet"
								image={Empty.PRESENTED_IMAGE_SIMPLE}
							/>
						) : (
							<List
								dataSource={upcomingMeetings}
								rowKey={(item) => item.id}
								renderItem={(event) => {
									const leadId = event.leadId;
									return (
										<List.Item
											actions={
												leadId
													? [
															<Button key="open-lead" size="small" onClick={() => onOpenLead(leadId)}>
																Åbn lead
															</Button>
														]
													: undefined
											}
										>
											<List.Item.Meta
												title={event.title || event.leadCompanyName || 'Møde'}
												description={
													<Space direction="vertical" size={0}>
														<Text strong>{formatDateTimeFull(event.startAt) || 'Ukendt tidspunkt'}</Text>
														{event.leadCompanyName && (
															<Text type="secondary">{event.leadCompanyName}</Text>
														)}
														{event.location && <Text type="secondary">{event.location}</Text>}
													</Space>
												}
											/>
										</List.Item>
									);
								}}
							/>
						)}
					</Card>
				</Col>

				{canManageTodos && (
					<Col xs={24} lg={12}>
						<Card
							title={`Kommende todos · næste ${todoWindowDays} dage`}
							extra={
								<Button type="link" size="small" onClick={onGoToTodos}>
									Se todos
								</Button>
							}
						>
							{upcomingTodosLoading ? (
								<div style={{ padding: 24, textAlign: 'center' }}>
									<Spin />
								</div>
							) : upcomingTodos.length === 0 ? (
								<Empty
									description="Ingen kommende opgaver i intervallet"
									image={Empty.PRESENTED_IMAGE_SIMPLE}
								/>
							) : (
								<Space direction="vertical" size={12} style={{ width: '100%' }}>
									{overdueTodos.length > 0 && (
										<Collapse
											items={[
												{
													key: 'overdue-todos',
													label: (
														<Text strong style={{ color: '#cf1322' }}>
															Overskredet deadline ({overdueTodos.length})
														</Text>
													),
													children: (
														<List
															dataSource={overdueTodos}
															rowKey={(item) => item.id}
															renderItem={renderTodoListItem}
														/>
													)
												}
											]}
										/>
									)}
									{nonOverdueTodos.length > 0 ? (
										<List
											dataSource={nonOverdueTodos}
											rowKey={(item) => item.id}
											renderItem={renderTodoListItem}
										/>
									) : null}
								</Space>
							)}
						</Card>
					</Col>
				)}
			</Row>

			<Row gutter={[16, 16]} className="dashboard-recent-leads">
				<Col xs={24}>
					<Card title="Største ejendomsporteføljer blandt leads">
						{propertyPortfoliosError ? (
							<Alert type="warning" showIcon message={propertyPortfoliosError} />
						) : propertyPortfoliosLoading ? (
							<div style={{ padding: 24, textAlign: 'center' }}><Spin /></div>
						) : propertyPortfolios.length === 0 ? (
							<Empty description="Ingen ejendomsporteføljer blandt synlige leads" image={Empty.PRESENTED_IMAGE_SIMPLE} />
						) : (
							<Table
								size="small"
								rowKey={(portfolio) => portfolio.leadId}
								dataSource={propertyPortfolios}
								pagination={false}
								scroll={{ x: 700 }}
								columns={[
									{
										title: 'Virksomhed',
										dataIndex: 'companyName',
										key: 'companyName',
										width: 260,
										render: (value: string | null, portfolio: DashboardPropertyPortfolio) => (
											<Button type="link" onClick={() => onOpenLead(portfolio.leadId)} style={{ padding: 0 }}>
												{value || portfolio.cvrnummer}
											</Button>
										)
									},
									{ title: 'CVR', dataIndex: 'cvrnummer', key: 'cvrnummer', width: 110 },
									{ title: 'Bygninger', dataIndex: 'buildingCount', key: 'buildingCount', width: 100 },
									{ title: 'Samlet areal', dataIndex: 'totalBuildingArea', key: 'totalBuildingArea', width: 140, render: formatArea },
									{ title: 'Erhvervsareal', dataIndex: 'totalCommercialArea', key: 'totalCommercialArea', width: 150, render: formatArea },
									{ title: 'Boligareal', dataIndex: 'totalResidentialArea', key: 'totalResidentialArea', width: 135, render: formatArea },
									{ title: 'Status', dataIndex: 'status', key: 'status', width: 155, render: (value: string | null) => value ? <Tag color={statusTagColors[value] || 'default'}>{statusLabelMap.get(value) || value}</Tag> : '-' }
								]}
							/>
						)}
					</Card>
				</Col>
				<Col xs={24}>
					<Card
						title="Sidst opdaterede leads"
						extra={
							<Checkbox
								checked={includeHiddenRecentLeads}
								onChange={(event) => onIncludeHiddenRecentLeadsChange(event.target.checked)}
							>
								Medtag skjulte leads
							</Checkbox>
						}
					>
						{recentLeadsLoading ? (
							<div style={{ padding: 24, textAlign: 'center' }}>
								<Spin />
							</div>
						) : recentLeads.length === 0 ? (
							<Empty description="Ingen leads fundet" image={Empty.PRESENTED_IMAGE_SIMPLE} />
						) : (
							<Table
								size="small"
								rowKey={(lead) => lead.id}
								dataSource={recentLeads}
								pagination={false}
								scroll={{ x: 760 }}
								columns={[
									{
										title: 'Virksomhed',
										dataIndex: 'company_name',
										key: 'company_name',
										width: 220,
										render: (value: string | null, lead: DashboardRecentLead) => (
											<Button type="link" onClick={() => onOpenLead(lead.id)} style={{ padding: 0 }}>
												{value || 'Ukendt virksomhed'}
											</Button>
										)
									},
									{ title: 'CVR', dataIndex: 'cvrnummer', key: 'cvrnummer', width: 110, render: (value: string | null) => value || '-' },
									{ title: 'Oprettet af', dataIndex: 'created_by_name', key: 'created_by_name', width: 160, render: (value: string | null) => value || '-' },
									{ title: 'Ændret af', dataIndex: 'updated_by_name', key: 'updated_by_name', width: 160, render: (value: string | null) => value || '-' },
									{
										title: 'Status',
										dataIndex: 'status',
										key: 'status',
										width: 140,
										render: (value: string | null) => value ? <Tag color={statusTagColors[value] || 'default'}>{statusLabelMap.get(value) || value}</Tag> : '-'
									},
									{
										title: 'Senest opdateret',
										key: 'updated_at',
										width: 150,
										render: (_: unknown, lead: DashboardRecentLead) => {
											const leadUpdatedAt = lead.updated_at || lead.created_at || null;
											const policyUpdatedAt = lead.last_policy_activity_at || null;
											const updatedAt = policyUpdatedAt && leadUpdatedAt && new Date(policyUpdatedAt).getTime() > new Date(leadUpdatedAt).getTime()
												? policyUpdatedAt
												: leadUpdatedAt || policyUpdatedAt;
											return formatDateTimeShort(updatedAt);
										}
									}
								]}
							/>
						)}
					</Card>
				</Col>
			</Row>

			{showPipelineHistory && (
				<Card
					title="Pipeline-status"
					extra={
						<Segmented
							size="small"
							value={pipelineHistoryWindow}
							options={PIPELINE_WINDOW_OPTIONS}
							onChange={(value) => onPipelineWindowChange(Number(value))}
						/>
					}
				>
					{pipelineHistoryError ? (
						<Alert type="error" message={pipelineHistoryError} showIcon />
					) : pipelineHistoryLoading ? (
						<div style={{ padding: 24, textAlign: 'center' }}>
							<Spin />
						</div>
					) : !hasPipelineCounts ? (
						<Empty
							description="Ingen pipeline-data i den valgte periode"
							image={Empty.PRESENTED_IMAGE_SIMPLE}
						/>
					) : (
						<Space direction="vertical" size={16} style={{ width: '100%' }}>
							<Space wrap>
								{pipelineSeries.map((series) => (
									<Tag
										key={series.status}
										style={{
											padding: '4px 12px',
											borderColor: series.color,
											borderWidth: 1,
											borderStyle: 'solid',
											backgroundColor: 'transparent',
											color: series.color
										}}
									>
										<Space size={8}>
											<span>{series.label}</span>
											<Text type="secondary" style={{ color: 'inherit' }}>
												{series.totalCount} i alt
											</Text>
										</Space>
									</Tag>
								))}
							</Space>
							<Column
								data={pipelineChartData}
								xField="day"
								yField="value"
								seriesField="label"
								colorField="status"
								isGroup
								color={({ status }: { status: string }) =>
									pipelineColorMap[status] || statusChartColors[status] || '#5B8FF9'}
								columnStyle={{ radius: [2, 2, 0, 0] }}
								legend={false}
								xAxis={{
									type: 'cat',
									label: {
										formatter: (value: string) => value.slice(5)
									}
								}}
								yAxis={{
									min: 0,
									nice: true
								}}
								tooltip={{
									shared: true,
									fields: ['label', 'value'],
									formatter: (datum: { label: string; value: number }) => ({
										name: datum.label,
										value: datum.value
									})
								}}
							/>
						</Space>
					)}
				</Card>
			)}
		</Space>
	);
}

